import $ from 'jquery';

import Asyncview from 'shopify-asyncview';
import morphdom from 'morphdom';
import rimg from '@pixelunion/rimg-shopify';
import $script from 'scriptjs';
import debounce from 'just-debounce';

/**
 * _getItemKey
 *
 * Checks a target and recursively checks parents until an element with a data-cart-item-key
 * is found, when the value of that attribute is returned.
 *
 * @param {HTMLElement} target Element from which to start search for item key (inclusive)
 */
function _getItemKey(target) {
  if (!target) {
    return null;
  }

  if (target.hasAttribute('data-cart-item-key')) {
    return target.dataset.cartItemKey;
  }

  return _getItemKey(target.parentNode);
}

export default class Cart {
  constructor() {
    this.init();
  }

  init() {
    this.theme = window.Theme;
    this.cartForm = document.querySelector('[data-cart-form]');

    if (this.cartForm) {
      this.cartTable = this.cartForm.querySelector('[data-cart-table]');
      this.cartDiscounts = this.cartForm.querySelector('[data-cart-discounts]');
      this.totalPrice = this.cartForm.querySelector('[data-cart-subtotal]');
    }

    this.$shippingSubmit = $('[data-calculate-shipping]');

    this.updateTimeout = null;

    const $scripts = $('[data-scripts]');
    const $shippingData = $('[data-shipping-calculator-data]');

    if ($shippingData.length) {
      try {
        this.theme.shippingCalculator = JSON.parse($shippingData.text());
      } catch (error) {
        console.warn('Error loading in Shipping calculator information', error);
      }
    }

    this.shopifyErrors = this._handleErrors.bind(this);
    this._handleClick = this._handleClick.bind(this);
    this._handleQtyChange = this._handleQtyChange.bind(this);
    this._handleQtyKeydown = this._handleQtyKeydown.bind(this);

    if (this.cartForm && this.cartForm.length) {
      $script($scripts.data('shopify-api-url'), () => {
        this._bindEvents();
      });
    }

    if (this.theme.shippingCalculator && this.$shippingSubmit.length) {
      $script([$scripts.data('shopify-countries'), $scripts.data('shopify-common')], () => {
        $script($scripts.data('shopify-cart'), () => {
          this._initShipping();
          Shopify.onError = this.shopifyErrors;
        });
      });
    }
  }

  _bindEvents() {
    this.cartForm.addEventListener('click', this._handleClick);
    this.cartForm.addEventListener('change', this._handleQtyChange);
    this.cartForm.addEventListener('keydown', this._handleQtyKeydown);

    $('[data-cart-notes]').on('change', debounce((event) => {
      const note = $(event.currentTarget).val();
      Shopify.updateCartNote(note, () => {
        // Without an empty call back, Shopify returns the amount of products
        // that are present in the cart. It checks against it being a function.
        return true;
      });
    }, 200));
  }

  /**
   * Gets the current value of the quantity input box for a given line item key
   *
   * @param {string} key
   */
  _getItemQuantity(key) {
    return parseInt(
      this.cartForm
        .querySelector(`[data-cart-item-key="${key}"] [data-cart-product-quantity]`)
        .value,
      10,
    );
  }

  /**
   * Sets a value for the quantity input box for a given line item key
   *
   * @param {string} key
   *
   * @param {integer} quantity
   */
  _setItemQuantity(key, quantity) {
    this.cartForm
      .querySelector(`[data-cart-item-key="${key}"] [data-cart-product-quantity]`)
      .value = quantity;
  }

  _handleClick(event) {
    if (!event.target.hasAttribute('data-cart-action')) {
      return;
    }

    const { cartAction } = event.target.dataset;
    const key = _getItemKey(event.target);
    const oldQuantity = this._getItemQuantity(key);
    let newQuantity;

    switch (cartAction) {
      case 'increment':
        newQuantity = oldQuantity + 1;
        break;

      case 'decrement':
        newQuantity = Math.max(oldQuantity - 1, 0);
        break;

      case 'remove':
        event.preventDefault();
        newQuantity = 0;
        break;

      default:
        // do nothing
        break;
    }

    this._setItemQuantity(key, newQuantity);
    this._updateItem(key, newQuantity);
  }

  _handleQtyChange(event) {
    if (!event.target.hasAttribute('data-cart-product-quantity')) {
      return;
    }

    const key = _getItemKey(event.target);
    const quantity = this._getItemQuantity(key);
    this._updateItem(key, quantity);
  }

  _handleQtyKeydown(event) {
    if (event.target.hasAttribute('data-cart-product-quantity') && event.key === 'Enter') {
      event.preventDefault();
      this._handleQtyChange(event);
    }
  }

  /**
   * Sends a request to the server to update a single product
   *
   * @param {string} key Shopify line item key (or product ID)
   *
   * @param {integer} quantity New quantity for item. If 0, item will be removed from cart.
   * @private
   */
  _updateItem(key, quantity) {
    // cancel any pending requests
    if (this.updateTimeout !== null) {
      clearTimeout(this.updateTimeout);
    }

    this.updateTimeout = setTimeout(() => {
      if (this._getItemQuantity(key) !== quantity) {
        this.updateTimeout = null;
        return;
      }

      const thisTimeoutId = this.updateTimeout;

      $.ajax({
        type: 'POST',
        url: window.Theme.routes.cart_change_url,
        dataType: 'json',
        data: {
          quantity,
          id: key,
        },
        success: (cart) => {
          if (this.updateTimeout !== thisTimeoutId) {
            return;
          }

          if (cart.item_count === 0) {
            // reload to show empty cart state
            window.location.reload();
            return;
          }

          this._updateCart(thisTimeoutId);
        },
      });
    }, 300);
  }

  /**
   * _updateCart
   *
   * Fetches new cart contents and swaps into page
   *
   * @param {integer} thisTimeoutId Id of timeout for this request. If no longer current, update is cancelled.
   */
  _updateCart(thisTimeoutId) {
    Asyncview.load(
      window.Theme.routes.cart_url,
      '_ajax',
    )
      .done(({ options, html }) => {
        // If another request is in progress, discard this update
        if (this.updateTimeout !== thisTimeoutId) {
          return;
        }

        // Inject new cart table contents
        const newTableContainer = document.createElement('div');
        newTableContainer.innerHTML = html.table;

        morphdom(
          this.cartTable,
          newTableContainer.querySelector('table'),
          {
            onBeforeElUpdated: (fromEl, toEl) => {
              // Skip images if src matches
              // - we don't want to reload lazy loaded images
              if (fromEl.tagName === 'IMG' && fromEl.src === toEl.src) {
                return false;
              }
              return true;
            },
          },
        );

        rimg.watch(this.cartTable);

        // Inject new cart level discounts
        this.cartDiscounts.innerHTML = html.discounts;

        // Update total
        this.totalPrice.innerHTML = options.totalPrice;
      })
      .fail(() => window.location.reload());
  }

  _initShipping() {
    this.$shippingResponse = $('[data-shipping-response]');
    this.$shippingResponseMessage = $('[data-shipping-response-message]');
    this.$shippingResponseRates = $('[data-shipping-response-rates]');

    this.$shippingSubmit.on('click', (event) => {
      event.preventDefault();
      this._calculateShipping();
    });

    // Prevents hitting 'enter' in these fields from accidental submitting the cart
    $('#address_zip').on('keypress', (event) => {
      if (event.keyCode === 10 || event.keyCode === 13) {
        event.preventDefault();
        this.$shippingSubmit.trigger('click');
      }
    });

    this._shippingCalculator();
  }

  _shippingCalculator() {
    Shopify.Cart.ShippingCalculator.show({
      moneyFormat: window.Theme.moneyFormat,
      submitButton: this.theme.shippingCalculator.submitButton,
      submitButtonDisabled: this.theme.shippingCalculator.submitButtonDisabled,
      wrapperId: 'shipping-calculator-response',
      customerIsLoggedIn: this.theme.customerLoggedIn,
    });
  }

  _calculateShipping() {
    this.$shippingSubmit
      .text(this.theme.shippingCalculator.submitButtonDisabled)
      .attr('disabled', true);

    const shippingAddress = {};
    shippingAddress.country = $('#address_country').val() || '';
    shippingAddress.province = $('#address_province').val() || '';
    shippingAddress.zip = $('#address_zip').val() || '';

    const queryString = Object.keys(shippingAddress)
      .map(key => `${encodeURIComponent(`shipping_address[${key}]`)}=${encodeURIComponent(shippingAddress[key])}`)
      .join('&');

    $.ajax(`/cart/shipping_rates.json?${queryString}`, { dataType: 'json' })
      .fail(error => this._handleErrors(error.responseJSON || {}))
      .done(response => {
        const rates = response.shipping_rates;

        let address = `${shippingAddress.zip}, ${shippingAddress.province}, ${shippingAddress.country}`;
        if (!shippingAddress.province.length) {
          address = `${shippingAddress.zip}, ${shippingAddress.country}`;
        }
        if (!shippingAddress.zip.length) {
          address = `${shippingAddress.province}, ${shippingAddress.country}`;
        }
        if (!shippingAddress.province.length || !shippingAddress.zip.length) {
          address = shippingAddress.country;
        }

        $('.wrapper-response', this.el)
          .empty()
          .append("<p class='shipping-calculator-response message'/><ul class='shipping-rates'/>");

        const ratesFeedback = $('.shipping-calculator-response', this.el);
        if (rates.length > 1) {
          const firstRate = Shopify.Cart.ShippingCalculator.formatRate(rates[0].price);
          const multipleShippingRates = window.Theme.shippingCalcMultiRates
            .replace('--address--', address)
            .replace('--number_of_rates--', rates.length)
            .replace('--rate--', `<span class='money'>${firstRate}</span>`);
          ratesFeedback.html(multipleShippingRates);
        } else if (rates.length === 1) {
          const oneShippingRate = window.Theme.shippingCalcOneRate
            .replace('--address--', address);
          ratesFeedback.html(oneShippingRate);
        } else {
          ratesFeedback.html(window.Theme.shippingCalcNoRates);
        }

        const $cartShippingRates = $('.cart-shipping-rates', this.el);
        $cartShippingRates.empty();

        for (let i = 0; i < rates.length; i++) {
          const rate = rates[i];
          const price = Shopify.Cart.ShippingCalculator.formatRate(rate.price);
          const rateValues = window.Theme.shippingCalcRateValues
            .replace('--rate_title--', rate.name)
            .replace('--rate--', `<span class='money'>${price}</span>`);

          $cartShippingRates.append(`<li>${rateValues}</li>`);
          // Reset the calculating button so it can be used again
          this._resetShippingButton();
          this.$shippingResponse.toggleClass('visible', true);
        }
      });
  }

  _resetShippingButton(){
    this.$shippingSubmit
        .text(this.theme.shippingCalculator.submitButton)
        .attr('disabled', false);
  }

  _handleErrors(errors) {
    let errorMessage = $.parseJSON(errors.responseText);

    if (errorMessage.zip.length > 0) {
      errorMessage = this.theme.shippingCalculator.errorMessage.replace('*error_message*', errorMessage.zip);
      this._alert(errorMessage);
      this._resetShippingButton();
    } else {
      console.warn(errorMessage);
    }
  }

  _alert(message) {
    $('[data-cart-message]').html(`<div class="cart-message">${message}</div>`);
  }
}
